import { useState } from 'react';
import type { FormEvent } from 'react';
import type { Me } from '../auth/authApi.ts';
import { changePassword, quotaLine, signOut } from '../lib/auth.ts';
import { asSentence } from '../lib/http.ts';
import { Field } from '../components/Field.tsx';
import {
  NewPasswordFields,
  newPasswordFault,
  newPasswordFieldId,
} from '../components/NewPasswordFields.tsx';
import type { NewPasswordFault } from '../components/NewPasswordFields.tsx';

const ID_PREFIX = 'account';
const CURRENT_ID = 'account-current-password';

/** `#/account` (docs/specs/accounts.md, section 8): change the password, sign out. */
export function AccountView({ me }: { me: Me }): JSX.Element {
  const quota = quotaLine(me);
  return (
    <div className="account-view">
      <header className="account-masthead">
        <h2 className="display account-title">Account</h2>
        <div className="account-meta">
          <span>{me.email}</span>
          {me.role === 'admin' && <span>admin</span>}
          {quota && <span>{quota}</span>}
        </div>
      </header>

      <section className="account-section">
        <div className="tick">
          <span className="label">§ Password</span>
        </div>
        <PasswordForm email={me.email} />
      </section>

      <section className="account-section">
        <div className="tick">
          <span className="label">§ This login</span>
        </div>
        <SignOutButton />
      </section>
    </div>
  );
}

function PasswordForm({ email }: { email: string }): JSX.Element {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [fault, setFault] = useState<NewPasswordFault | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setDone(false);
    setCurrentError(null);
    setFault(null);
    if (!current) {
      setCurrentError('Enter your current password.');
      document.getElementById(CURRENT_ID)?.focus();
      return;
    }
    const found = newPasswordFault(password, confirm);
    if (found) {
      setFault(found);
      document.getElementById(newPasswordFieldId(ID_PREFIX, found.field))?.focus();
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, password);
      setCurrent('');
      setPassword('');
      setConfirm('');
      setDone(true);
    } catch (e) {
      // A 400 says which it was: a wrong current password, or a refused new one. The server's
      // words are the only thing that tells the two apart, so they go in the form's notice and
      // not on a field this code would have to guess.
      setError(asSentence(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" noValidate onSubmit={(e) => void onSubmit(e)}>
      {/* Not shown, but read: it tells a password manager which login the new password is for. */}
      <input type="email" name="email" autoComplete="username" value={email} readOnly hidden />
      <Field
        id={CURRENT_ID}
        label="Current password"
        type="password"
        autoComplete="current-password"
        required
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        error={currentError}
      />
      <NewPasswordFields
        idPrefix={ID_PREFIX}
        password={password}
        confirm={confirm}
        fault={fault}
        onPassword={setPassword}
        onConfirm={setConfirm}
      />
      {error && (
        <p className="notice err" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p className="notice ok" role="status">
          Password changed. Every other login of this account is signed out.
        </p>
      )}
      <button className="btn primary" type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Change password'}
      </button>
    </form>
  );
}

function SignOutButton(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await signOut();
    } catch (e) {
      setError(`Sign-out failed: ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <>
      {error && (
        <p className="notice err" role="alert">
          {error}
        </p>
      )}
      <button className="btn" type="button" disabled={busy} onClick={() => void onClick()}>
        {busy ? 'Signing out…' : 'Sign out'}
      </button>
    </>
  );
}
